/**
 * The question bank (E15): what employers ask, against what you have
 * answered. Every fill records the questions a form asked; the common ones
 * JobCork cannot answer from your profile are listed here. Answer once, and
 * every later form that asks the same thing gets it -- in your words,
 * before it is left for you. A choice control gets your variants in order.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "./auth";
import { ConfirmRemove } from "./fields";

interface BankEntry {
  norm: string;
  sample: string;
  control: string;
  options: string[];
  fieldKey: string | null;
  timesSeen: number;
  requiredSeen: number;
  employers: string[];
  mine: string[] | null;
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.messages.join(" ");
  return err instanceof Error ? err.message : String(err);
}

function askedWords(e: BankEntry): string {
  if (e.timesSeen === 0) return "your saved answer";
  const times = e.timesSeen === 1 ? "asked once" : `asked ${e.timesSeen} times`;
  const by = e.employers.length > 0 ? ` by ${e.employers.length === 1 ? e.employers[0] : `${e.employers.length} employers`}` : "";
  const req = e.requiredSeen > 0 ? ", required" : "";
  return `${times}${by}${req}`;
}

function Editor({ entry, onSaved, onRemoved }: { entry: BankEntry; onSaved: (answers: string[]) => void; onRemoved: () => void }) {
  const [answers, setAnswers] = useState<string[]>(entry.mine ?? [""]);
  const [editing, setEditing] = useState(entry.mine === null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isChoice = entry.control === "select" || entry.control === "radio" || entry.control === "combobox";

  const save = async () => {
    const clean = answers.map((a) => a.trim()).filter(Boolean);
    if (clean.length === 0) { setError("An answer is needed."); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ norm: string; answers: string[] }>("/api/questions/answers", { method: "PUT", body: JSON.stringify({ norm: entry.norm, answers: clean }) });
      setAnswers(r.answers);
      setEditing(false);
      onSaved(r.answers);
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api("/api/questions/answers", { method: "DELETE", body: JSON.stringify({ norm: entry.norm }) });
      setAnswers([""]);
      setEditing(true);
      onRemoved();
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  };

  if (!editing && entry.mine) {
    return (
      <div className="bank-answer">
        <p className="small"><span className="tag ok-tag">Answered</span> {entry.mine.join(" · ")}</p>
        <p className="small">
          <button className="step-link small" disabled={busy} onClick={() => setEditing(true)}>Change</button>
          {" · "}
          <ConfirmRemove onRemove={() => void remove()} busy={busy} />
        </p>
        {error && <p className="err small">{error}</p>}
      </div>
    );
  }

  return (
    <div className="bank-answer">
      {answers.map((a, i) => (
        <div key={i} className="bank-variant">
          {isChoice && entry.options.length > 0 ? (
            <select className="input" value={entry.options.includes(a) ? a : ""} disabled={busy}
                    onChange={(e) => setAnswers(answers.map((x, j) => (j === i ? e.target.value : x)))}>
              <option value="">{i === 0 ? "Pick the option you would choose" : "Another option you would accept"}</option>
              {entry.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <textarea className="input notes" rows={entry.control === "textarea" ? 3 : 1} value={a} disabled={busy}
                      placeholder={i === 0 ? "Your answer, in your words" : "Another acceptable answer"}
                      onChange={(e) => setAnswers(answers.map((x, j) => (j === i ? e.target.value : x)))} />
          )}
          {answers.length > 1 && (
            <button className="step-link small" disabled={busy} onClick={() => setAnswers(answers.filter((_, j) => j !== i))}>Drop</button>
          )}
        </div>
      ))}
      <p className="small">
        <button className="btn small" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        {" "}
        {answers.length < 5 && (
          <button className="step-link small" disabled={busy} onClick={() => setAnswers([...answers, ""])}>
            {isChoice ? "Add another option you would accept" : "Add a variant"}
          </button>
        )}
        {entry.mine && (
          <>{" · "}<button className="step-link small" disabled={busy} onClick={() => { setAnswers(entry.mine ?? [""]); setEditing(false); }}>Cancel</button></>
        )}
      </p>
      {error && <p className="err small">{error}</p>}
    </div>
  );
}

export function QuestionBank() {
  const [entries, setEntries] = useState<BankEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    api<{ questions: BankEntry[] }>("/api/questions").then((r) => setEntries(r.questions)).catch((e) => setError(describe(e)));
  }, []);
  useEffect(load, [load]);

  if (error) return <p className="err small">{error}</p>;
  if (!entries) return null;

  // The gaps first: what employers ask that the profile does not answer. What JobCork already answers is folded away.
  const gaps = entries.filter((e) => !e.fieldKey || e.mine);
  const covered = entries.filter((e) => e.fieldKey && !e.mine).length;
  const answered = gaps.filter((e) => e.mine).length;
  const shown = showAll ? gaps : gaps.slice(0, 12);

  return (
    <section className="bank">
      <h2>Questions employers ask</h2>
      <p className="muted small">
        Every form JobCork fills teaches it what gets asked. The ones below are what your profile does not cover.
        Answer once, in your words; every later form that asks the same thing gets your answer before it is left for you.
        {covered > 0 ? ` ${covered} other common question${covered === 1 ? " is" : "s are"} already answered from your profile.` : ""}
        {answered > 0 ? ` You have answered ${answered}.` : ""}
      </p>
      {gaps.length === 0 && <p className="muted small">Nothing yet. Fill a form or two and the questions show up here.</p>}
      <ul className="bank-list">
        {shown.map((e) => (
          <li key={e.norm} className={e.mine ? "answered" : ""}>
            <p className="bank-q">{e.sample}</p>
            <p className="muted small">{askedWords(e)}{e.options.length > 0 ? ` · offers: ${e.options.slice(0, 6).join(" / ")}${e.options.length > 6 ? " …" : ""}` : ""}</p>
            <Editor
              entry={e}
              onSaved={(answers) => setEntries((list) => list && list.map((x) => (x.norm === e.norm ? { ...x, mine: answers } : x)))}
              onRemoved={() => setEntries((list) => list && list.map((x) => (x.norm === e.norm ? { ...x, mine: null } : x)))}
            />
          </li>
        ))}
      </ul>
      {gaps.length > 12 && (
        <p className="small"><button className="step-link small" onClick={() => setShowAll(!showAll)}>{showAll ? "Show fewer" : `Show all ${gaps.length}`}</button></p>
      )}
    </section>
  );
}
